import { makeAutoObservable, runInAction } from 'mobx'
import { numberFormat } from 'shared/lib'
import {
  type IResponseSubscription,
  type IResponseSubscriptionSuggestion,
  type ISubscriptionSeats,
  type ISubscriptionStatuses,
} from 'entities/Subscription/api/types'
import { SubscriptionApi } from 'entities/Subscription/api/subscription'
import { SubscriptionPlan } from 'entities/Subscription/model'
import { TrialSubscriptionStore } from 'entities/Subscription/store/trialSubscriptionStore'
import type { EntitiesStore } from 'entities/EntitiesExport'

export class SubscriptionStore {
  initPromise
  private _setFinishInit: ((value?: unknown) => void) | null = null
  private _loading = false
  private _trialActiveContactsCount = 250

  private _id: number | null = null
  private _plan: SubscriptionPlan | null = null
  private _planCredits: number | null = null
  private _status: ISubscriptionStatuses | null = null
  private _scheduledPlanId: string | null = null
  private _scheduled_date: string | null = null
  private _scheduledIsDowngrade: boolean | null = null
  private _periodEndsAt: string | null = null
  private _isScheduledCancellation = false
  private _subscriptionSuggestion: IResponseSubscriptionSuggestion | null = null
  private _numbers: Null<IResponseSubscription['numbers']> = null
  private _seats: ISubscriptionSeats | null = null
  private _defaultPaymentMethodId: string | null = null

  setSeats = (seats: ISubscriptionSeats) => {
    this._seats = seats
  }
  private _trialCredits = 0
  private _trialSubscriptionStore = new TrialSubscriptionStore(this)

  constructor(private _entities: EntitiesStore) {
    makeAutoObservable(this)
    this.initPromise = new Promise((resolve) => {
      this._setFinishInit = resolve
    })
  }

  get entities() {
    return this._entities
  }

  get trialSubscriptionStore() {
    return this._trialSubscriptionStore
  }

  get id() {
    return this._id
  }

  get plan() {
    return this._plan
  }

  get planCredits() {
    return this._planCredits
  }

  get status() {
    return this._status
  }

  get scheduledPlanId() {
    return this._scheduledPlanId
  }
  get scheduled_date() {
    return this._scheduled_date
  }

  get defaultPaymentMethodId() {
    return this._defaultPaymentMethodId
  }

  get scheduledIsDowngrade() {
    return this._scheduledIsDowngrade
  }

  get periodEndsAt() {
    return this._periodEndsAt
  }

  get isScheduledCancellation() {
    return this._isScheduledCancellation
  }

  setIsScheduledCancellation = (value: boolean) => {
    this._isScheduledCancellation = value
  }

  get loading() {
    return this._loading
  }

  get trialCredits() {
    return this._trialCredits
  }

  get subscriptionSuggestion() {
    return this._subscriptionSuggestion
  }

  get trialActiveContactsCount() {
    return this._trialActiveContactsCount
  }

  get formattedTrialCredits() {
    return numberFormat({ value: this._trialCredits })
  }

  get isTrial() {
    return this._status === 'trialing'
  }

  get isActive() {
    return this._status === 'active'
  }

  get isPastDue() {
    return this._status === 'past_due'
  }

  private _applySubscription = (subscription: IResponseSubscription) => {
    this._id = subscription.id
    this._plan = new SubscriptionPlan(subscription.plan)
    this._status = subscription.status
    this._trialCredits = subscription.trial_credits
    this._planCredits = subscription.plan_credits
    this._subscriptionSuggestion = subscription.subscription_suggestion
    this._numbers = subscription.numbers
    this._seats = subscription.seats
    this._scheduledPlanId = subscription.scheduled_plan_id
    this._scheduled_date = subscription.scheduled_date
    this._scheduledIsDowngrade = subscription.scheduled_is_downgrade
    this._periodEndsAt = subscription.period_ends_at
    this._isScheduledCancellation = subscription.is_scheduled_cancellation
    this._defaultPaymentMethodId = subscription.default_payment_method_id
  }

  private _fetchSubscription = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data: subscription } = await SubscriptionApi.getSubscription()
      this._applySubscription(subscription)
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  init = async () => {
    await this._fetchSubscription()
    this._setFinishInit?.()
  }

  syncFromEvent = (subscription: IResponseSubscription) => {
    this._applySubscription(subscription)
  }

  get isAgencyWithSubscription() {
    if (!this._status) return false

    const availableStatuses: ISubscriptionStatuses[] = ['active', 'trialing']

    return this._entities.organizationStore.isAgency && availableStatuses.includes(this._status)
  }

  get isAgencyWithoutSubscription() {
    if (!this._status) return true

    const availableStatuses: ISubscriptionStatuses[] = ['active', 'past_due', 'trialing']

    return this._entities.organizationStore.isAgency && !availableStatuses.includes(this._status)
  }

  get isCanceled() {
    return this._status === 'canceled' || this._status === 'incomplete_expired'
  }

  get isSuspended() {
    return this._status === 'suspended'
  }

  get isUnpaid() {
    return this._status === 'unpaid' || this._status === 'past_due'
  }

  get isPlanActive() {
    return Boolean(this._plan?.active)
  }

  get isPAYGPlan() {
    return this._plan?.id === 'free-plan-monthly'
  }

  get numbers() {
    return this._numbers
  }

  get numbersTotal() {
    return this._numbers?.total || 0
  }

  get licensesCount() {
    return this.numbers ? this.numbers.total - this.numbers.used : 0
  }

  get seatsTotal() {
    return this._seats?.total ?? 0
  }

  get seatsUsed() {
    return this._seats?.used ?? 0
  }
  get seatsAvailable() {
    return this.seatsTotal - this.seatsUsed
  }

  updateSeats = () => {
    return this._fetchSubscription()
  }
}
